<template>
  <div
    :class="cn('flex w-full items-start gap-4 py-4', { 'text-white': white })"
  >
    <div class="flex max-md:flex-col items-start gap-4">
      <nuxt-link
        class="block w-36 min-w-36 shrink-0"
        :to="productUrl"
        @click="closeCart"
      >
        <UiLazyImg
          :src="product.image_url || '/images/no-image.webp'"
          :alt="product.title || product.currentTranslation?.title"
          class="bg-product-card-gradient"
        />
      </nuxt-link>
      <div>
        <nuxt-link
          class="text-sm uppercase font-bold"
          :to="productUrl"
          @click="closeCart"
        >
          {{ product.title }}
        </nuxt-link>
        <div class="mt-4 mb-3 text-sm space-y-1">
          <div
            v-if="product?.embedded"
            v-html="product.product_short_description"
          />
          <template v-else-if="!isDiamond">
            <div
              v-for="material in productMaterials"
              :key="material.category_title"
            >
              {{ material.category_title }}: {{ material.title }}
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="ml-auto">
      <div class="mb-4 text-right text-sm font-medium">
        <span v-if="Number(product.start_at_cost)" class="line-through mr-1">
          {{ $n(Number(product.start_at_cost), "currency") }}
        </span>
        <span>
          {{
            $n(
              Number(product.sale ? product.sale_cost : product.base_cost),
              "currency",
            )
          }}
        </span>
      </div>
      <div class="flex flex-col">
        <UiNumberInput
          v-if="!hideControls"
          v-model="internalQuantity"
          class="mb-2"
          input-classes="w-8 text-center"
          :min="1"
          :white="white"
          :max="product.track_inventory ? product.stock_qty || 0 : 999"
          :disabled="
            !!isGiftProduct(product) ||
            checkoutStore.removingIdsLoading.includes(product.id)
          "
        />
        <div v-else class="mb-4 text-sm text-right">
          {{ $t("qty") }}: {{ internalQuantity }}
        </div>

        <UiButton
          v-if="!hideControls"
          variant="text"
          :color="white ? 'white' : 'primary'"
          class="underline p-0 min-h-0 normal-case ml-auto font-normal"
          :loading="checkoutStore.removingIdsLoading.includes(product.id)"
          :disabled="!!isGiftProduct(product)"
          size="sm"
          @click="removeCartItemWrap"
        >
          {{ $t("remove") }}
        </UiButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CartItem } from "ecom-types";
import { diamondSku } from "@/configs";

type ProductMaterial = {
  category_title: string;
  title: string;
  order: number;
};

const props = defineProps<{
  product: CartItem;
  hideMenu?: boolean;
  showEstimation?: boolean;
  isChild?: boolean;
  productIndex?: number;
  hideControls?: boolean;
  white?: boolean;
}>();

defineEmits<{
  (e: "showMore"): void;
  (e: "remove"): void;
}>();

const checkoutStore = useCheckoutStore();

const localePath = useLocalePathPolyfill();

const { closeCart, removeCartItem, updateCartItem, isGiftProduct } = useCart();

const { removeFromCartEvent } = useEvents();

const internalQuantity = ref(props.product.quantity);

const unwatch = watchDebounced(
  internalQuantity,
  () => {
    if (
      !internalQuantity.value ||
      checkoutStore.removingIdsLoading.includes(props.product.id)
    )
      return;
    updateCartItem({ ...props.product, quantity: internalQuantity.value });
  },
  { debounce: 500 },
);

watch(
  () => props.product.quantity,
  (newQuantity) => {
    internalQuantity.value = newQuantity;
  },
);

const productUrl = computed(() => {
  if (props.product.options.engagement) {
    if (props.product.options.engagement.product_type === "diamond") {
      return localePath({
        path: `/product/${props.product.slug}`,
        query: {
          configuration_id: props.product.configuration_id,
          pmaterials: props.product.options.engagement.pmaterials,
          dsku: props.product.options.engagement.dsku,
          psku: props.product.options.engagement.psku,
          dShape: props.product.options.engagement.dShape || "",
          dCarat: props.product.options.engagement.dCarat,
          pAllowedShapes: props.product.options.engagement.pAllowedShapes,
          pAllowedCarats: props.product.options.engagement.pAllowedCarats,
          startWith: props.product.options.engagement.startWith,
          flowType: props.product.options.engagement.flowType,
        },
      });
    } else if (props.product.options.engagement.product_type === "product") {
      return localePath({
        path: `/product/${props.product.slug}`,
        query: {
          configuration_id: props.product.configuration_id,
          materials: props.product.options.engagement.pmaterials,
          pmaterials: props.product.options.engagement.pmaterials,
          dsku: props.product.options.engagement.dsku,
          psku: props.product.options.engagement.psku,
          dShape: props.product.options.engagement.dShape || "",
          dCarat: props.product.options.engagement.dCarat,
          pAllowedShapes: props.product.options.engagement.pAllowedShapes,
          pAllowedCarats: props.product.options.engagement.pAllowedCarats,
          startWith: props.product.options.engagement.startWith,
          flowType: props.product.options.engagement.flowType,
        },
      });
    } else {
      return localePath({
        path: "/engagement-ring-settings/review",
        query: {
          psku: props.product.options.engagement.psku,
          dsku: props.product.options.engagement.dsku,
          pmaterials: props.product.options.engagement.pmaterials,
          dShape: props.product.options.engagement.dShape || "",
          dCarat: props.product.options.engagement.dCarat,
          pAllowedShapes: props.product.options.engagement.pAllowedShapes,
          pAllowedCarats: props.product.options.engagement.pAllowedCarats,
          startWith: props.product.options.engagement.startWith,
          flowType: props.product.options.engagement.flowType,
        },
      });
    }
  } else {
    return localePath(
      `/product/${props.product.slug}?configuration_id=${props.product.configuration_id}`,
    );
  }
});

const isDiamond = computed(() => {
  return !!props.product?.categories?.find((el) => el.sku === diamondSku);
});

const filteredProductMaterials = computed<ProductMaterial[]>(() => {
  const res = props.product.materials.reduce((acc, material, currentIndex) => {
    // const constant = cartMaterialShowListSKU.find(
    //   (item) => item.sku === material.category_sku,
    // );
    //
    // if (
    //   constant?.sku === propertiesGroupSkus.stoneSize ||
    //   constant?.sku === propertiesGroupSkus.caratWeight
    // ) {
    //   acc.push({ title: material.title + " Ct. Tw.", order: constant.order });
    // } else if (constant) {
    //   acc.push({ title: material.title, order: constant.order });
    // }

    acc.push({
      category_title: material.category_title,
      title: material.title,
      order: currentIndex,
    });

    return acc;
  }, [] as ProductMaterial[]);

  res.sort((a, b) => a.order - b.order);

  return res;
});

const filteredProductOptionsMaterials = computed<ProductMaterial[]>(() => {
  return (props.product?.options?.materials || []).map((material, idx) => {
    return {
      category_title: material.material_category_title,
      title: material.title,
      order: idx,
    };
  });
  // const optionRingSize = props.product?.options?.materials?.find(
  //   (material) => material.material_category_sku?.sku === ringSizeSku,
  // );
  //
  // if (optionRingSize) {
  //   return `${filteredProductMaterials.value ? " | " : ""}Size ${optionRingSize.title}`;
  // }
  //
  // return "";
});

const productMaterials = computed<ProductMaterial[]>(() => {
  return [
    ...filteredProductMaterials.value,
    ...filteredProductOptionsMaterials.value,
  ];
});

async function removeCartItemWrap() {
  await removeCartItem(
    props.product.id,
    props.product.diamond ? props.product.diamond[0].id : undefined,
  );
  unwatch();
  removeFromCartEvent({
    product_id: props.product.product_id,
    configuration_id: props.product.configuration_id,
    collection_id: props.product?.collections_ids?.[0] || "",
    product_sku: props.product.vendor_model,
    product_price: props.product?.base_cost,
    productData: props.product,
    productIndex: props.productIndex || 0,
  });
  if (props.product.diamond?.length) {
    removeFromCartEvent({
      product_id: props.product.diamond[0].id,
      configuration_id: props.product.diamond[0].configuration_id,
      collection_id: props.product.diamond[0]?.collections_ids?.[0] || "",
      product_sku: props.product.diamond[0].vendor_model,
      product_price: props.product.diamond[0]?.base_cost,
      productData: props.product.diamond[0],
      productIndex: props.productIndex || 0,
    });
  }
}
</script>
